const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || 'http://localhost:3001/api';

class ApiService {
  constructor() {
    this.token = localStorage.getItem('authToken');
  }

  getAuthHeaders() {
    const headers = {
      'Content-Type': 'application/json',
    };
    
    if (this.token) {
      headers.Authorization = `Bearer ${this.token}`;
    }
    
    return headers;
  }

  setToken(token) {
    this.token = token;
    if (token) {
      localStorage.setItem('authToken', token);
    } else {
      localStorage.removeItem('authToken');
    }
  }

  async request(endpoint, options = {}) {
    const url = `${API_BASE_URL}${endpoint}`;
    const config = {
      headers: {
        ...this.getAuthHeaders(),
        ...options.headers,
      },
      ...options,
    };

    if (config.body && typeof config.body === 'object') {
      config.body = JSON.stringify(config.body);
    }

    try {
      const response = await fetch(url, config);

      // Handle authentication errors
      if (response.status === 401) {
        this.setToken(null);
        // Don't redirect in SPA, let the AuthContext handle it
        throw new Error('Authentication required');
      }

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.error || `API Error: ${response.status} ${response.statusText}`);
      }

      // Handle 204 No Content responses
      if (response.status === 204) {
        return null;
      }

      return await response.json();
    } catch (error) {
      if (error.name === 'TypeError' && error.message.includes('fetch')) {
        throw new Error('Unable to connect to server. Please check if the backend is running on http://localhost:3001');
      }
      if (error.message.includes('Failed to fetch')) {
        throw new Error('Backend server is not running. Please start the backend server.');
      }
      throw error;
    }
  }

  // Health check method
  async healthCheck() {
    try {
      const response = await fetch(`${API_BASE_URL}/health`);
      if (!response.ok) {
        throw new Error(`Health check failed: ${response.status}`);
      }
      return await response.json();
    } catch (error) {
      throw new Error('Backend server is not responding. Please start the backend server.');
    }
  }

  // Authentication
  async register(userData) {
    console.log('ApiService: Registering user with data:', userData);
    const response = await this.request('/auth/register', {
      method: 'POST',
      body: userData,
    });
    
    console.log('ApiService: Registration response:', response);
    
    if (response.token) {
      this.setToken(response.token);
      console.log('ApiService: Token set after registration');
    }
    
    return response;
  }

  async login(credentials) {
    console.log('ApiService: Logging in user with email:', credentials.email);
    const response = await this.request('/auth/login', {
      method: 'POST',
      body: credentials,
    });
    
    console.log('ApiService: Login response:', response);
    
    if (response.token) {
      this.setToken(response.token);
      console.log('ApiService: Token set after login');
    }
    
    return response;
  }

  async getCurrentUser() {
    return this.request('/auth/me');
  }

  async updateUser(updates) {
    const response = await this.request('/auth/me', {
      method: 'PUT',
      body: updates,
    });
    
    // Extract the user object from the response
    return response.user || response;
  }

  async updatePrivacySettings(privacySettings) {
    return this.request('/auth/privacy-settings', {
      method: 'PUT',
      body: { privacySettings },
    });
  }

  async updateProfile(updates) {
    return this.updateUser(updates);
  }

  async deleteAccount() {
    return this.request('/auth/delete-account', {
      method: 'DELETE',
    });
  }

  // Custom Categories
  async addCustomCategory(category) {
    return this.request('/categories', {
      method: 'POST',
      body: { category },
    });
  }

  async deleteCustomCategory(category) {
    return this.request(`/categories/${encodeURIComponent(category)}`, {
      method: 'DELETE',
    });
  }

  logout() {
    this.setToken(null);
  }

  isAuthenticated() {
    return !!this.token;
  }

  // Shopping Lists
  async getLists() {
    return this.request('/lists');
  }

  async getList(id) {
    return this.request(`/lists/${id}`);
  }

  async createList(listData) {
    return this.request('/lists', {
      method: 'POST',
      body: listData,
    });
  }

  async updateList(id, updates) {
    return this.request(`/lists/${id}`, {
      method: 'PUT',
      body: updates,
    });
  }

  async deleteList(id) {
    return this.request(`/lists/${id}`, {
      method: 'DELETE',
    });
  }

  async getListStats(id) {
    return this.request(`/lists/${id}/stats`);
  }

  async getUserStats() {
    console.log('🔄 Frontend: Calling getUserStats API...');
    try {
      const result = await this.request('/user/stats');
      console.log('✅ Frontend: getUserStats API response:', result);
      return result;
    } catch (error) {
      console.error('❌ Frontend: getUserStats API error:', error);
      throw error;
    }
  }

  // Items
  async addItem(listId, itemData) {
    return this.request(`/lists/${listId}/items`, {
      method: 'POST',
      body: itemData,
    });
  }

  async updateItem(listId, itemId, updates) {
    return this.request(`/lists/${listId}/items/${itemId}`, {
      method: 'PUT',
      body: updates,
    });
  }

  async deleteItem(listId, itemId) {
    return this.request(`/lists/${listId}/items/${itemId}`, {
      method: 'DELETE',
    });
  }

  // List management
  async clearAllItems(listId) {
    return this.request(`/lists/${listId}/items`, {
      method: 'DELETE',
    });
  }

  async clearCheckedItems(listId) {
    return this.request(`/lists/${listId}/items/checked`, {
      method: 'DELETE',
    });
  }

  async uncheckAllItems(listId) {
    return this.request(`/lists/${listId}/uncheck-all`, {
      method: 'POST',
    });
  }

  // Sharing
  async shareList(listId, email, permission = 'read') {
    return this.request(`/lists/${listId}/share`, {
      method: 'POST',
      body: { email, permission },
    });
  }

  async removeListAccess(listId, userId) {
    return this.request(`/lists/${listId}/share/${userId}`, {
      method: 'DELETE',
    });
  }

  async updateListPermission(listId, userId, permission) {
    return this.request(`/lists/${listId}/share/${userId}`, {
      method: 'PUT',
      body: { permission },
    });
  }

  async transferListOwnership(listId, newOwnerId) {
    return this.request(`/lists/${listId}/transfer`, {
      method: 'PATCH',
      body: { newOwnerId },
    });
  }

  async unfollowList(listId) {
    return this.request(`/lists/${listId}/unfollow`, {
      method: 'DELETE',
    });
  }

  async searchUsers(query) {
    const endpoint = `/users/search?q=${encodeURIComponent(query)}`;
    return this.request(endpoint);
  }

  async findUserByEmail(email) {
    const endpoint = `/users/find-by-email?email=${encodeURIComponent(email)}`;
    return this.request(endpoint);
  }

  // Learning System APIs
  async getSmartSuggestions(searchTerm = '', limit = 10) {
    return this.request(`/smart-suggestions?search=${encodeURIComponent(searchTerm)}&limit=${limit}`);
  }

  async seedItemDatabase(templateItems) {
    return this.request('/seed-item-database', { 
      method: 'POST',
      body: { templateItems }
    });
  }

  async getLearnedCategory(itemName) {
    return this.request(`/learned-category/${encodeURIComponent(itemName)}`);
  }

  async getCategorySuggestions(searchTerm = '', limit = 8) {
    return this.request(`/category-suggestions?search=${encodeURIComponent(searchTerm)}&limit=${limit}`);
  }

  async seedLearningData() {
    return this.request('/seed-learning-data', { method: 'POST' });
  }

  async getQuickAddItems(listId) {
    if (!listId) {
      throw new Error('List ID is required for quick add items');
    }
    return this.request(`/quick-add-items/${listId}`);
  }

  async updateQuickAddItems(listId, items) {
    if (!listId) {
      throw new Error('List ID is required for updating quick add items');
    }
    return this.request(`/quick-add-items/${listId}`, {
      method: 'POST',
      body: JSON.stringify({ items }),
    });
  }

  async getQuickAddPreferences() {
    return this.request('/quick-add-preferences');
  }

  async updateQuickAddPreferences(preferences) {
    return this.request('/quick-add-preferences', {
      method: 'POST',
      body: JSON.stringify(preferences),
    });
  }

  async getLearningStats() {
    return this.request('/learning-stats');
  }

  // Admin APIs
  async getAdminStats() {
    return this.request('/admin/stats');
  }

  async getAllUsers() {
    return this.request('/admin/users');
  }

  async getAllLists() {
    return this.request('/admin/lists');
  }

  async getListDetailsAsAdmin(listId) {
    console.log('apiService.getListDetailsAsAdmin called with listId:', listId);
    const endpoint = `/admin/lists/${listId}`;
    console.log('Making request to endpoint:', endpoint);
    return this.request(endpoint);
  }

  async deleteUser(userId) {
    return this.request(`/admin/users/${userId}`, {
      method: 'DELETE'
    });
  }

  async toggleUserAdmin(userId, isAdmin) {
    return this.request(`/admin/users/${userId}/admin`, {
      method: 'PATCH',
      body: { isAdmin }
    });
  }

  async updateUserAdmin(userId, isAdmin) {
    return this.request(`/admin/users/${userId}/admin`, {
      method: 'PATCH',
      body: { isAdmin }
    });
  }

  async toggleUserBlocked(userId, isBlocked) {
    return this.request(`/admin/users/${userId}/block`, {
      method: 'PATCH',
      body: { isBlocked }
    });
  }

  async toggleListBlock(listId, isBlocked) {
    return this.request(`/admin/lists/${listId}/block`, {
      method: 'PATCH',
      body: { isBlocked }
    });
  }

  // Export user data
  async exportUserData() {
    console.log('🔄 Requesting user data export...');
    
    try {
      const response = await fetch(`${API_BASE_URL}/user/export`, {
        method: 'GET',
        headers: this.getAuthHeaders()
      });

      if (!response.ok) {
        throw new Error(`Export failed: ${response.status} ${response.statusText}`);
      }

      // Get the filename from the Content-Disposition header
      const contentDisposition = response.headers.get('Content-Disposition');
      let filename = 'listwiz-export.json';
      
      console.log('🔍 Content-Disposition header:', contentDisposition);
      
      if (contentDisposition) {
        // Try to match filename with quotes first, then without quotes
        const filenameMatch = contentDisposition.match(/filename="([^"]+)"/) || 
                             contentDisposition.match(/filename=([^;\s]+)/);
        if (filenameMatch) {
          filename = filenameMatch[1];
          console.log('📁 Extracted filename:', filename);
        } else {
          console.warn('⚠️ Could not extract filename from:', contentDisposition);
        }
      } else {
        console.warn('⚠️ No Content-Disposition header found');
      }

      // Get the JSON data
      const exportData = await response.json();
      
      console.log('✅ Export data received:', {
        lists: exportData.lists?.length || 0,
        items: exportData.statistics?.totalItems || 0,
        size: JSON.stringify(exportData).length
      });

      // Create a blob and download it
      const blob = new Blob([JSON.stringify(exportData, null, 2)], { 
        type: 'application/json' 
      });
      
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = filename;
      
      // Trigger download
      document.body.appendChild(link);
      link.click();
      
      // Cleanup
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
      
      console.log('💾 File downloaded successfully:', filename);
      return { success: true, filename, data: exportData };
      
    } catch (error) {
      console.error('❌ Export failed:', error);
      throw error;
    }
  }

  // ===== RECENT ITEMS METHODS =====

  async getRecentItems(listId) {
    return this.request(`/lists/${listId}/recent-items`);
  }

  async getCategories(listId) {
    return this.request(`/lists/${listId}/categories`);
  }

  async getUserCategories() {
    return this.request('/user/categories');
  }

  async updateItemCategory(listId, itemId, category) {
    return this.request(`/lists/${listId}/items/${itemId}/category`, {
      method: 'PUT',
      body: { category }
    });
  }

  // ===== LEARNED ITEMS MANAGEMENT =====

  async getLearnedItems() {
    return this.request('/learned-items');
  }

  async deleteLearnedItem(itemName) {
    return this.request(`/learned-items/${encodeURIComponent(itemName)}`, {
      method: 'DELETE'
    });
  }

  async deleteMultipleLearnedItems(itemNames) {
    return this.request('/learned-items', {
      method: 'DELETE',
      body: { itemNames }
    });
  }

  // ===== DATA RETENTION METHODS =====

  async getDataRetentionStats() {
    return this.request('/admin/data-retention/stats');
  }

  async getDataRetentionConfig() {
    return this.request('/admin/data-retention/config');
  }

  async runDataRetentionCleanup() {
    return this.request('/admin/data-retention/cleanup', {
      method: 'POST'
    });
  }

  // Display preferences methods
  async getDisplayPreferences() {
    return this.request('/display/preferences');
  }

  async updateDisplayPreferences(preferences) {
    return this.request('/display/preferences', {
      method: 'PUT',
      body: preferences
    });
  }

  async getListDisplaySettings(listId) {
    return this.request(`/lists/${listId}/display-settings`);
  }

  async updateListDisplaySettings(listId, settings) {
    return this.request(`/lists/${listId}/display-settings`, {
      method: 'PUT',
      body: settings
    });
  }
}

export const apiService = new ApiService();
